import type BaseEnumType from "@/app/services/baseEnumType";
import type {
  AssemblyHeader,
  AssemblySummary,
} from "@/app/services/generated/assemblyService";
import {
  AssemblyFilter,
  assemblyService,
} from "@/app/services/generated/assemblyService";
import { EnumAssemblyStatus } from "@/app/services/generated/enums/enumAssemblyStatus.generated";
import { EnumAssemblyType } from "@/app/services/generated/enums/enumAssemblyType.generated";
import type { EnumOutputMovementType } from "@/components/pages/production/assemblies/composables/enumOutputMovementType";
import { makeRef, withLoading } from "@/components/composables/componentTools";
import { translate, keys } from "@/plugins/i18n";
import type { Ref } from "vue";
import { computed, onMounted, ref, shallowRef, watch } from "vue";
import type { Recipe } from "@/app/services/generated/recipeService";
import { recipeService } from "@/app/services/generated/recipeService";
import type Guid from "@/app/types/common/guid";
import {
  sortAndMapRecipes,
  type RecipeWithName,
} from "@/components/pages/production/assemblies/composables/createAssembly";
import apiCache from "@/app/caching/apiCache";
import type { IHasItemsCache } from "@/app/caching/caches/itemsCache";
import ItemsCache from "@/app/caching/caches/itemsCache";
import Key from "@/app/types/common/key";
import type { Maybe } from "@/components/utils/typeHelpers";
import type { Item } from "@/app/services/generated/itemService";

// Used to fix issue with objects using 'header'
export interface WrappedRecipe {
  recipe: Recipe;
  name: string;
}

export interface AssemblyWithTitle {
  title: string; // assembly / batch / product
  assemblySummary: AssemblySummary;
}

export function buildAssemblyHeaderTitle(
  assemblyHeader: AssemblyHeader,
  itemsCache: ItemsCache
) {
  const stockItem = itemsCache.getById(
    assemblyHeader.outputStillExpected?.stockItemId
  )?.details.name;

  return translate(keys.assembly_batch_product_joiner, {
    assembly: assemblyHeader.name,
    batch: assemblyHeader.urn || "-",
    product: stockItem ?? "-",
  }).toString();
}

export function useOutputToExistingAssembly(
  selectedMovementType: Ref<EnumOutputMovementType>,
  sourceAssemblyId: Guid
) {
  const targetAssembly = ref<AssemblyWithTitle>();
  const availableTargetAssemblies = ref([]) as Ref<AssemblyWithTitle[]>;

  // We should filter our target assemblies by our selected movement type
  const filteredAvailableTargetAssemblies = computed(() => {
    return availableTargetAssemblies.value.filter(
      (assemblyWithTitle) =>
        assemblyWithTitle.assemblySummary.header.assemblyType ===
          selectedMovementType.value.assemblyType &&
        !assemblyWithTitle.assemblySummary.header.id.equals(sourceAssemblyId)
    );
  });

  onMounted(
    withLoading(async () => {
      const cache = apiCache.buildCacheSet({
        items: ItemsCache,
      });

      // Only get in progress assemblies
      const assemblyFilter = new AssemblyFilter();
      assemblyFilter.statuses = [EnumAssemblyStatus.inProgress];

      const [visibleInProgressAssemblies] = await Promise.all([
        assemblyService
          .getFilteredAssemblySummaries(assemblyFilter)
          .then((summaries) =>
            summaries.assemblySummaries.filter(
              (summary) => !summary.header.isHidden
            )
          ),
        cache.update(),
      ]);

      availableTargetAssemblies.value = visibleInProgressAssemblies
        // Joins the assemblySummary to have a title (assembly / batch / product)
        .map((assemblySummary): AssemblyWithTitle => {
          return {
            title: buildAssemblyHeaderTitle(
              assemblySummary.header,
              cache.items
            ),
            assemblySummary,
          };
        });
    })
  );

  return {
    filteredAvailableTargetAssemblies,
    targetAssembly,
  };
}

export interface AddOutputToNewAssemblyVariables {
  targetAssemblyRecipe: Recipe | undefined;
  targetAssemblyType: EnumAssemblyType;
  targetAssemblyStockItem: Maybe<{ id: Guid; unitOfMeasureId: Guid }>;
  targetExpectedOutputQuantity: number | undefined;
  updateTargetAssemblyIngredientsByExpectedOutputMultiplier: boolean;
}

export function useOutputToNewAssembly(
  selectedMovementType: Ref<EnumOutputMovementType>,
  cache: IHasItemsCache
) {
  const targetAssemblyType = makeRef(
    selectedMovementType.value.assemblyType ??
      EnumAssemblyType.materialProduction
  );
  const targetAssemblyRecipe = ref<RecipeWithName | undefined>();
  const targetAssemblyStockItem = makeRef<Maybe<Item>>(null);
  const allRecipes = shallowRef<Recipe[]>([]);

  const availableTargetRecipes = computed(() =>
    sortAndMapRecipes(
      allRecipes.value,
      targetAssemblyType.value,
      targetAssemblyStockItem.value?.details.id
    )
  );

  const availableTargetAssemblyStockItems = computed(() =>
    cache.items.all.filter((stockItem) => {
      return targetAssemblyType.value.isMaterialProduction()
        ? stockItem.details.mainPurpose.isMaterial()
        : stockItem.details.mainPurpose.isProduct();
    })
  );

  // If our selected recipe changes, update our output stock item to match the recipe
  watch(
    targetAssemblyRecipe,
    () => {
      targetAssemblyStockItem.value = cache.items.getById(
        targetAssemblyRecipe.value?.recipe.header.output?.stockItemId
      );
    },
    { immediate: true }
  );

  // If we change our assembly type, make sure we reset the values so that the DD's aren't empty
  watch(targetAssemblyType, () => {
    targetAssemblyStockItem.value = null;
    targetAssemblyRecipe.value = undefined;
  });

  // Fetch recipe and stockItem data
  onMounted(
    withLoading(async () => {
      allRecipes.value = await recipeService.getAllRecipes();
    })
  );

  const targetExpectedOutputQuantity = ref<number>();
  const updateTargetAssemblyIngredientsByExpectedOutputMultiplier =
    ref<boolean>(true);

  const resetUseOutputToNewAssemblyState = () => {
    targetExpectedOutputQuantity.value = undefined;
    updateTargetAssemblyIngredientsByExpectedOutputMultiplier.value = true;
  };

  // Returns the 'add-to-new-assembly' event-specific variables
  const createOutputToNewAssemblyEventObject =
    (): AddOutputToNewAssemblyVariables => {
      return {
        targetAssemblyRecipe: targetAssemblyRecipe.value?.recipe as
          | Recipe
          | undefined,
        targetAssemblyType: targetAssemblyType.value as EnumAssemblyType,
        targetAssemblyStockItem: targetAssemblyStockItem.value?.details,
        targetExpectedOutputQuantity: targetExpectedOutputQuantity.value,
        updateTargetAssemblyIngredientsByExpectedOutputMultiplier:
          updateTargetAssemblyIngredientsByExpectedOutputMultiplier.value,
      };
    };

  // If we change movement type, we need to clear these values as they will no longer be valid
  watch(selectedMovementType, () => {
    targetAssemblyStockItem.value = null;
    targetAssemblyRecipe.value = undefined;

    // When we change our movement type, we need to update the internal assembly type
    if (selectedMovementType.value.assemblyType !== undefined) {
      targetAssemblyType.value = selectedMovementType.value
        .assemblyType as EnumAssemblyType;
    }
  });

  return {
    targetAssemblyRecipe,
    targetAssemblyStockItem,
    availableTargetRecipes,
    availableTargetAssemblyStockItems,
    targetExpectedOutputQuantity,
    updateTargetAssemblyIngredientsByExpectedOutputMultiplier,
    createOutputToNewAssemblyEventObject,
    resetUseOutputToNewAssemblyState,
  };
}

export class EnumAssemblyOutputByType implements BaseEnumType {
  private static allValues: EnumAssemblyOutputByType[] = []; // make sure this is top-most

  public static QtyFromCurrentAssembly = new EnumAssemblyOutputByType(
    0,
    "QtyFromCurrentAssembly",
    "enum_assembly_output_by_current_assembly"
  );

  public static UnitsToCreate = new EnumAssemblyOutputByType(
    1,
    "UnitsToCreate",
    "enum_assembly_output_by_units_to_create"
  );

  public key: number;
  public value: string;
  public translationKey?: string;

  private constructor(key: number, value: string, translationKey?: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumAssemblyOutputByType.allValues.push(this);
  }

  public equals(other: Maybe<EnumAssemblyOutputByType>): boolean {
    return this.key === other?.key;
  }

  public isQtyFromCurrentAssembly() {
    return this.equals(EnumAssemblyOutputByType.QtyFromCurrentAssembly);
  }
  public isUnitsToCreate() {
    return this.equals(EnumAssemblyOutputByType.UnitsToCreate);
  }

  public static getValues(): EnumAssemblyOutputByType[] {
    return EnumAssemblyOutputByType.allValues;
  }

  public static getByKey(key: number): EnumAssemblyOutputByType {
    const found = EnumAssemblyOutputByType.allValues.find((e) => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumAssemblyOutputByType - Key not found: ${key}`);
    }

    return found;
  }
}

export class EnumAssemblyOutputCostPriceType implements BaseEnumType {
  private static allValues: EnumAssemblyOutputCostPriceType[] = []; // make sure this is top-most

  public static CurrentProductCostPrice = new EnumAssemblyOutputCostPriceType(
    0,
    "CurrentProductCostPrice",
    "enum_assembly_output_cost_price_current"
  );

  public static UsedIngredientsCostPrice = new EnumAssemblyOutputCostPriceType(
    1,
    "UsedIngredientsCostPrice",
    "enum_assembly_output_cost_price_used_ingredients"
  );

  public static CustomCostPrice = new EnumAssemblyOutputCostPriceType(
    2,
    "CustomCostPrice",
    "enum_assembly_output_cost_price_custom"
  );

  public key: number;
  public value: string;
  public translationKey: string;
  public get translation() {
    return translate(Key.fromString(this.translationKey));
  }

  private constructor(key: number, value: string, translationKey: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumAssemblyOutputCostPriceType.allValues.push(this);
  }

  public equals(other: Maybe<EnumAssemblyOutputCostPriceType>): boolean {
    return this.key === other?.key;
  }

  public isCurrentProductCostPrice() {
    return this.equals(EnumAssemblyOutputCostPriceType.CurrentProductCostPrice);
  }
  public isUsedIngredientsCostPrice() {
    return this.equals(
      EnumAssemblyOutputCostPriceType.UsedIngredientsCostPrice
    );
  }
  public isCustomCostPrice() {
    return this.equals(EnumAssemblyOutputCostPriceType.CustomCostPrice);
  }

  public static getValues(): EnumAssemblyOutputCostPriceType[] {
    return EnumAssemblyOutputCostPriceType.allValues;
  }

  public static getByKey(key: number): EnumAssemblyOutputCostPriceType {
    const found = EnumAssemblyOutputCostPriceType.allValues.find(
      (e) => e.key === key
    );
    if (found === undefined) {
      throw new Error(
        `EnumAssemblyOutputCostPriceType - Key not found: ${key}`
      );
    }

    return found;
  }
}
